"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.LogLevel = exports.DEFAULT_LOG_LEVEL = void 0;
exports.getDebugLogger = getDebugLogger;
exports.getErrorLogger = getErrorLogger;
exports.getInfoLogger = getInfoLogger;
exports.getLogLevel = getLogLevel;
exports.getLogger = getLogger;
exports.getLoggers = getLoggers;
exports.getWarningLogger = getWarningLogger;
exports.log = log;
exports.logMethodsMap = void 0;
var _isSSR = require("./isSSR");
/* eslint-disable no-console -- simple logger */
/**
 * Log messages verbosity level which can be dynamically set also on production environment.
 *
 * Please, consider using Info level for rather important checks in the app workflow
 * (like data load success, user action logs, etc.).
 *
 * Use Debug level for other less important messages. This is for the possibility
 * to have the generic app flow still checked in log without necessarily overcrowding
 * it with all the debug message logs.
 */
let LogLevel = exports.LogLevel = /*#__PURE__*/function (LogLevel) {
  LogLevel[LogLevel["None"] = 0] = "None";
  LogLevel[LogLevel["Error"] = 1] = "Error";
  LogLevel[LogLevel["Warning"] = 2] = "Warning";
  LogLevel[LogLevel["Info"] = 3] = "Info";
  LogLevel[LogLevel["Debug"] = 4] = "Debug";
  return LogLevel;
}({});
/**
 * The default log level is set to warnings, which means errors & warnings
 * are logged automatically without any need to change the settings.
 *
 * For info & debug messages to be logged, the settings have to be changed manually.
 * It can be done either in the local testing page UI, or in JS console via:
 * localStorage.setItem(`${appName} debug`, 3); // LogLevel.Info for example
 */
const DEFAULT_LOG_LEVEL = exports.DEFAULT_LOG_LEVEL = LogLevel.Warning;

/**
 * Default color of application name in the log message. It is possible
 * to override this in the getLogger methods. A different color assigned
 * to logs from different apps can make basic orientation in console logs
 * easier, considering usage of multiple app logs in parallel.
 */
const DEFAULT_APP_LABEL_COLOR = 'c90';

/**
 * Current log methods map. So far, the console methods are used,
 * but the methods are replaceable, so for example AppInsights logger
 * might be implemented additionally to the existing functionality.
 */
const logMethodsMap = exports.logMethodsMap = new Map([[LogLevel.None, (...args) => {}], [LogLevel.Error, console.error], [LogLevel.Warning, console.warn], [LogLevel.Info, console.info], [LogLevel.Debug, console.log]]);
function getTime() {
  const date = new Date();
  const minutes = `0${date.getMinutes()}`;
  const seconds = `0${date.getSeconds()}`;
  const milliseconds = `00${date.getMilliseconds()}`;
  const LAST_TWO_DIGITS = -2;
  const LAST_THREE_DIGITS = -3;
  return `${date.getHours()}:${minutes.slice(LAST_TWO_DIGITS)}:${seconds.slice(LAST_TWO_DIGITS)}.${milliseconds.slice(LAST_THREE_DIGITS)}`;
}

/**
 * @param appName Application name & logging key, also part of the log level setting storage key.
 * @returns Current logging level for the specified application.
 */
function getLogLevel(appName) {
  let result = !(0, _isSSR.isSSR)() && Boolean(window.localStorage) && Number(JSON.parse(localStorage.getItem(`${appName} debug`) ?? String(DEFAULT_LOG_LEVEL)));
  if (typeof result !== 'number' || Number.isNaN(result) || result < LogLevel.None || result > LogLevel.Debug) {
    result = DEFAULT_LOG_LEVEL;
  }
  return result;
}

/**
 * Generic log method wrapper. Formats the logged output a bit.
 * Skips calling the specific log method when current application log level
 * is lower than the requested log level in this call.
 * @param appName Application name / logging key.
 * @param level Requested log level for this call.
 * @param color Application name color in the log message.
 * @param args Log message / data.
 */
// eslint-disable-next-line max-params -- low level function not expected to be referenced outside of this module
function log(appName, level, color, ...args) {
  const currentLevel = getLogLevel(appName);
  if (currentLevel < level) {
    return;
  }
  const time = getTime();
  const usedColor = `color: #${color};`;
  const method = logMethodsMap.get(level);
  method?.(`%c${time} %c${appName}:`, 'color: #09c;', usedColor, ...args);
}

/**
 * @param appName Application name / logger key.
 * @param logLevel Log messages verbosity level.
 * @param color Application name color in the log message.
 * @returns Logger function for the specified log level.
 */
function getLogger(appName, logLevel, color = DEFAULT_APP_LABEL_COLOR) {
  return (...args) => log(appName, logLevel, color, ...args);
}

/**
 * @param appName Application name / logger key.
 * @param color Application name color in the log message.
 * @returns Error logger function.
 */
function getErrorLogger(appName, color = DEFAULT_APP_LABEL_COLOR) {
  return getLogger(appName, LogLevel.Error, color);
}

/**
 * @param appName Application name / logger key.
 * @param color Application name color in the log message.
 * @returns Warning logger function.
 */
function getWarningLogger(appName, color = DEFAULT_APP_LABEL_COLOR) {
  return getLogger(appName, LogLevel.Warning, color);
}

/**
 * @param appName Application name / logger key.
 * @param color Application name color in the log message.
 * @returns Info logger function.
 */
function getInfoLogger(appName, color = DEFAULT_APP_LABEL_COLOR) {
  return getLogger(appName, LogLevel.Info, color);
}

/**
 * @param appName Application name / logger key.
 * @param color Application name color in the log message.
 * @returns Debug logger function.
 */
function getDebugLogger(appName, color = DEFAULT_APP_LABEL_COLOR) {
  return getLogger(appName, LogLevel.Debug, color);
}

/**
 * @param appName Application name / logger key.
 * @param color Application name color in the log message.
 * @returns All log levels' loggers when needed.
 */
function getLoggers(appName, color = DEFAULT_APP_LABEL_COLOR) {
  return {
    logDebug: getDebugLogger(appName, color),
    logError: getErrorLogger(appName, color),
    logInfo: getInfoLogger(appName, color),
    logWarning: getWarningLogger(appName, color)
  };
}
